import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { PerfilPadrao, type OpcoesConviteDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { Dialogo } from '../design-system/Dialogo';

const PERFIS: Record<PerfilPadrao, { rotulo: string; descricao: string }> = {
  [PerfilPadrao.Colaborador]: { rotulo: 'Colaborador', descricao: 'trabalha nas licitações' },
  [PerfilPadrao.Gestor]: { rotulo: 'Gestor', descricao: 'coordena a equipe e acompanha as notas' },
  [PerfilPadrao.Leitor]: { rotulo: 'Leitor', descricao: 'só acompanha' },
  [PerfilPadrao.Administrador]: { rotulo: 'Administrador', descricao: 'tudo, inclusive convidar qualquer perfil' },
};
const ORDEM = [PerfilPadrao.Colaborador, PerfilPadrao.Gestor, PerfilPadrao.Leitor, PerfilPadrao.Administrador];
const COM_GESTOR = new Set([PerfilPadrao.Colaborador, PerfilPadrao.Leitor]);

type Props = { opcoes: OpcoesConviteDto; aoFechar: (mensagem?: string) => void };

const mensagemDe = (situacao: string, email: string) => {
  if (situacao === 'adicionado') return `${email} já tinha conta e entrou no quadro.`;
  if (situacao === 'aguardando') return `Convite para ${email} enviado para a aprovação do seu gestor.`;
  return `Convite enviado para ${email}.`;
};

const rotuloEnviar = (opcoes: OpcoesConviteDto) => (opcoes.exigeAprovacao ? 'Pedir aprovação' : 'Enviar convite');

/** Gestor direto de colaborador e leitor: só o administrador escolhe (ADR-0021). */
function EscolhaGestor({ opcoes, gestor, definir }: { opcoes: OpcoesConviteDto; gestor: string; definir: (uuid: string) => void }) {
  if (opcoes.gestores.length === 0) return null;
  return (
    <label className="campo">
      <span>Gestor direto</span>
      <select value={gestor} onChange={(e) => definir(e.target.value)}>
        <option value="">Eu mesmo (administrador)</option>
        {opcoes.gestores.map((g) => (
          <option key={g.uuid} value={g.uuid}>
            {g.nome}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Convidar para o quadro (RQ-ACE-003, ADR-0021): os perfis que a pessoa pode convidar, o gestor direto quando o
 * administrador convida colaborador ou leitor e o aviso de aprovação quando quem convida é colaborador.
 */
export function DialogoConvidar({ opcoes, aoFechar }: Props) {
  const { espacos } = useDependencias();
  const perfis = ORDEM.filter((p) => opcoes.perfis.includes(p));
  const [email, definirEmail] = useState('');
  const [perfil, definirPerfil] = useState(perfis[0] ?? PerfilPadrao.Colaborador);
  const [gestor, definirGestor] = useState('');
  const [erro, definirErro] = useState<string>();
  const [enviando, definirEnviando] = useState(false);

  const convidar = async () => {
    definirEnviando(true);
    try {
      const r = await espacos.convidar({ email, perfil, gestorUuid: COM_GESTOR.has(perfil) && gestor ? gestor : undefined });
      aoFechar(mensagemDe(r.situacao, email));
    } catch (e) {
      definirErro((e as Error).message);
      definirEnviando(false);
    }
  };

  return (
    <Dialogo
      titulo="Convidar para o quadro"
      descricao="A pessoa recebe um e-mail para criar a senha ou entrar com o Google. O link vale por 7 dias."
      rotuloConfirmar={enviando ? 'Enviando…' : rotuloEnviar(opcoes)}
      podeConfirmar={!enviando && /^\S+@\S+\.\S+$/.test(email)}
      aoConfirmar={() => void convidar()}
      aoCancelar={() => aoFechar()}
    >
      {opcoes.exigeAprovacao && (
        <p className="dialogo__aviso" role="note">
          <Info size={16} aria-hidden /> O convite passa pela aprovação do seu gestor (ou do administrador) antes de ser enviado. O pedido vale por 7 dias.
        </p>
      )}
      <label className="campo">
        <span>E-mail</span>
        <input type="email" autoComplete="off" value={email} onChange={(e) => definirEmail(e.target.value.trim())} autoFocus />
      </label>
      <fieldset className="campo-opcoes">
        <legend>Perfil no quadro</legend>
        {perfis.map((p) => (
          <label key={p} className="campo-opcoes__item">
            <input type="radio" name="perfil" checked={perfil === p} onChange={() => definirPerfil(p)} />
            <span>
              <strong>{PERFIS[p].rotulo}</strong> · {PERFIS[p].descricao}
            </span>
          </label>
        ))}
      </fieldset>
      {COM_GESTOR.has(perfil) && <EscolhaGestor opcoes={opcoes} gestor={gestor} definir={definirGestor} />}
      {erro && (
        <p className="cartao-acesso__erro" role="alert">
          {erro}
        </p>
      )}
    </Dialogo>
  );
}

/** Carrega o que a pessoa pode convidar (null enquanto carrega; leitor recebe lista vazia). */
export function useOpcoesConvite(ativo: boolean) {
  const { espacos } = useDependencias();
  const [opcoes, definir] = useState<OpcoesConviteDto | null>(null);
  useEffect(() => {
    if (!ativo || opcoes) return;
    espacos.opcoesConvite().then(definir, () => definir({ perfis: [], gestores: [], exigeAprovacao: false }));
  }, [ativo, opcoes, espacos]);
  return opcoes;
}
